import React, { useEffect, useMemo, useState } from "react";
import { FaMapMarkerAlt } from "react-icons/fa";
import client from "../api/client";
import "./LocationSetting.css";
import { MapContainer, TileLayer, Marker, Circle, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const formatCoord = (value) => {
  if (value === "" || value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  return Number(value).toFixed(6);
};

const defaultCenter = { lat: 20.5937, lng: 78.9629 };
const defaultZoom = 5;
const focusZoom = 13;

const MapClickHandler = ({ onSelect }) => {
  useMapEvents({
    click(e) {
      onSelect(e.latlng);
    },
  });
  return null;
};

export default function LocationSetting() {
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [radius, setRadius] = useState(100);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const res = await client.get("/location/get");
        const payload = res.data || {};
        if (payload.latitude !== undefined) setLatitude(payload.latitude.toString());
        if (payload.longitude !== undefined) setLongitude(payload.longitude.toString());
        if (payload.radius !== undefined) setRadius(payload.radius);
        if (payload.updatedAt) setLastUpdated(new Date(payload.updatedAt).toLocaleString());
        if (payload.latitude !== undefined && payload.longitude !== undefined) {
          setSelectedPosition({
            lat: Number(payload.latitude),
            lng: Number(payload.longitude),
          });
        }
      } catch (err) {
        console.error("fetch location failed", err);
        setStatus("Unable to load saved location.");
      } finally {
        setLoading(false);
      }
    };

    fetchLocation();
  }, []);

  const handleUseDeviceLocation = () => {
    if (!navigator.geolocation) {
      setStatus("Geolocation is not supported by your browser.");
      return;
    }

    setStatus("Fetching current location…");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatitude(position.coords.latitude.toFixed(6));
        setLongitude(position.coords.longitude.toFixed(6));
        setSelectedPosition({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setStatus("Location captured from your device.");
      },
      (err) => {
        console.error("geolocation error", err);
        setStatus(`Unable to access location: ${err.message}`);
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setStatus("");
    const latNum = Number(latitude);
    const lngNum = Number(longitude);
    const radiusNum = Number(radius);
    const normalizedRadius = Number.isNaN(radiusNum) || radiusNum <= 0 ? 100 : radiusNum;

    if (!selectedPosition) {
      setStatus("Please pick a location on the map before saving.");
      return;
    }

    if (Number.isNaN(latNum) || Number.isNaN(lngNum)) {
      setStatus("Latitude and longitude must be valid numbers.");
      return;
    }

    try {
      setSaving(true);
      await client.post("/location/set", {
        latitude: latNum,
        longitude: lngNum,
        radius: normalizedRadius,
      });
      setLastUpdated(new Date().toLocaleString());
      setSelectedPosition({ lat: latNum, lng: lngNum });
      setStatus("Office location saved.");
    } catch (err) {
      console.error("save location failed", err);
      setStatus("Failed to save location. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleMapSelect = ({ lat, lng }) => {
    const formattedLat = lat.toFixed(6);
    const formattedLng = lng.toFixed(6);
    setLatitude(formattedLat);
    setLongitude(formattedLng);
    setSelectedPosition({ lat, lng });
    setStatus("");
  };

  useEffect(() => {
    if (!mapInstance) return;
    const target = selectedPosition || defaultCenter;
    const zoom = selectedPosition ? focusZoom : defaultZoom;
    mapInstance.setView([target.lat, target.lng], zoom);
    mapInstance.invalidateSize();
  }, [mapInstance, selectedPosition]);

  const circleRadius = useMemo(() => {
    const parsed = Number(radius);
    return Number.isNaN(parsed) || parsed <= 0 ? 100 : parsed;
  }, [radius]);

  return (
    <div className="location-page page-container">
      <header className="location-header">
        <div className="location-title">
          <FaMapMarkerAlt />
          <div>
            <h1>Location Setting</h1>
            <p>Define the office coordinates and radius for auto check-in validation.</p>
          </div>
        </div>
        <div className="location-header-actions">
          <button className="btn outline small" onClick={handleUseDeviceLocation} type="button">
            Use Current Location
          </button>
          <span className="location-note">
            {loading
              ? "Loading saved location…"
              : lastUpdated
              ? `Last saved: ${lastUpdated}`
              : "No saved location yet"}
          </span>
        </div>
      </header>

      <form className="location-card" onSubmit={handleSave}>
        <MapContainer
          center={selectedPosition || defaultCenter}
          zoom={selectedPosition ? focusZoom : defaultZoom}
          className="location-map"
          whenCreated={setMapInstance}
          scrollWheelZoom
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
          <MapClickHandler onSelect={handleMapSelect} />
          {selectedPosition && (
            <>
              <Marker
                position={selectedPosition}
                draggable
                eventHandlers={{
                  dragend: (e) => {
                    const latlng = e.target.getLatLng();
                    handleMapSelect(latlng);
                  },
                }}
              />
              <Circle
                center={selectedPosition}
                radius={circleRadius}
                pathOptions={{ color: "#2563eb", fillColor: "#2563eb", fillOpacity: 0.1 }}
              />
            </>
          )}
        </MapContainer>

        <div className="selected-location">
          Selected Location: <strong>{formatCoord(latitude)}, {formatCoord(longitude)}</strong>
        </div>

        <div className="form-grid radius-only">
          <label>
            Radius (meters)
            <input
              type="number"
              min="10"
              step="5"
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
            />
          </label>
        </div>

        {status && <p className="location-status">{status}</p>}

        <div className="location-actions">
          <button className="btn primary" type="submit" disabled={saving}>
            {saving ? "Saving…" : "Save Location"}
          </button>
          <span className="location-hint">
            Office point: {formatCoord(latitude)} , {formatCoord(longitude)} · Radius {radius || 100}m
          </span>
        </div>
      </form>
    </div>
  );
}
